import React from "react";
import { OutboxSummary } from "../../../types/outbox.types";
import { LaneBadge, LANE_ORDER } from "./LaneBadge";

interface SummaryCardsProps {
  summary: OutboxSummary;
}

const PENDING_WARNING_THRESHOLD = 100;
const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60_000;

function formatAge(ms: number | null): string {
  if (ms === null) return "-";
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(0)}s`;
  return `${(ms / MS_PER_MINUTE).toFixed(1)}min`;
}

function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms === 0) return "-";
  if (ms < MS_PER_SECOND) return `${ms.toFixed(0)}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(1)}s`;
  return `${(ms / MS_PER_MINUTE).toFixed(1)}min`;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  const { pending, processing, processedLast24h, failed, avgProcessingTimeMs, avgQueueWaitMs, avgProcessingMs, perLane } = summary;

  const pendingCardClass = pending > PENDING_WARNING_THRESHOLD
    ? "bg-amber-50 border-amber-200"
    : "bg-white border-neutral-200";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {/* Pending */}
        <div
          data-testid="outbox-summary-pending"
          className={`rounded-lg border p-6 ${pendingCardClass}`}
        >
          <div className="text-sm text-neutral-600 mb-1">Pending</div>
          <div className="text-2xl font-bold text-neutral-900">
            {pending.toLocaleString()}
          </div>
        </div>

        {/* Processing */}
        <div
          data-testid="outbox-summary-processing"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Processing</div>
          <div className="text-2xl font-bold text-neutral-900">
            {processing.toLocaleString()}
          </div>
        </div>

        {/* Processed (24h) */}
        <div
          data-testid="outbox-summary-processed"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Processed (24h)</div>
          <div className="text-2xl font-bold text-neutral-900">
            {processedLast24h.toLocaleString()}
          </div>
        </div>

        {/* Failed */}
        <div
          data-testid="outbox-summary-failed"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Failed</div>
          <div className="text-2xl font-bold text-red-600">
            {failed.toLocaleString()}
          </div>
        </div>

        {/* Avg Queue Wait */}
        <div
          data-testid="outbox-summary-avg-queue-wait"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Avg Queue Wait</div>
          <div className="text-2xl font-bold text-neutral-900">
            {formatDuration(avgQueueWaitMs)}
          </div>
        </div>

        {/* Avg Processing (claimed_at -> processed_at) */}
        <div
          data-testid="outbox-summary-avg-processing"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Avg Processing</div>
          <div className="text-2xl font-bold text-neutral-900">
            {formatDuration(avgProcessingMs)}
          </div>
        </div>

        {/* Avg End-to-End (created_at -> processed_at) */}
        <div
          data-testid="outbox-summary-avg-time"
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="text-sm text-neutral-600 mb-1">Avg End-to-End</div>
          <div className="text-2xl font-bold text-neutral-900">
            {formatDuration(avgProcessingTimeMs)}
          </div>
        </div>
      </div>

      {perLane && (
        <div
          className="bg-white rounded-lg border border-neutral-200 p-6"
          data-testid="outbox-per-lane"
        >
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Per Lane</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Lane
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Processing
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Oldest Pending
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {LANE_ORDER.map((lane) => {
                  const data = perLane[lane];
                  if (!data) return null;
                  const rowClass = data.pending > PENDING_WARNING_THRESHOLD ? "bg-amber-50" : "";
                  return (
                    <tr key={lane} data-testid={`lane-row-${lane}`} className={`hover:bg-neutral-50 ${rowClass}`}>
                      <td className="px-4 py-3 text-sm">
                        <LaneBadge lane={lane} />
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                        {data.pending.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                        {data.processing.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                        {formatAge(data.oldestPendingAgeMs)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryCards;
